import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import DraggableFloater from "./DraggableFloater";

/**
 * QuickWidgetLauncher — Floating "+" button that pops a compact grid of every
 * widget kind. Owner brief (Feb 2026): "the page that that button opens —
 * why does it open off the page and I can't move it?" — so the popup is now
 * wrapped in DraggableFloater (user can drag it) AND clamped to the viewport
 * on render so on a narrow window (or with browser dev-tools docked) it can't
 * spill off the left edge.
 */
const QUICK_WIDGETS = [
  { kind: "live_users",     icon: "👥", label: "Live Users",        pos: "top_right",     size: "medium", extra: { app_key: "all" } },
  { kind: "weather",        icon: "☀",  label: "Weather (live)",    pos: "bottom_right",  size: "large" },
  { kind: "clock",          icon: "🕒", label: "Clock",             pos: "top_left",      size: "medium" },
  { kind: "date",           icon: "📅", label: "Date",              pos: "top_left",      size: "small" },
  { kind: "btc_ticker",     icon: "₿",  label: "BTC",               pos: "top_right",     size: "medium" },
  { kind: "eth_ticker",     icon: "Ξ",  label: "ETH",               pos: "top_right",     size: "medium" },
  { kind: "gold_ticker",    icon: "🥇", label: "Gold",              pos: "top_right",     size: "medium" },
  { kind: "moon_phase",     icon: "🌙", label: "Moon Phase",        pos: "top_right",     size: "small" },
  { kind: "illuminati_eye", icon: "👁",  label: "All-Seeing Eye",    pos: "top_center",    size: "large" },
  { kind: "quote",          icon: "❝",  label: "Daily Quote",       pos: "bottom_center", size: "medium", extra: { text: "Stay focused." } },
];

// Compute a safe default popup position so it never spills off the viewport
// on narrow OR short windows. Width ~260px, height up to 70vh (per panel
// style). We anchor so the bottom-right corner sits 80px from the bottom-
// right of the screen, but clamp to a min margin of 8px so it never goes
// negative.
function safePopupInitial() {
  if (typeof window === "undefined") return { right: 80, bottom: 80 };
  const w = window.innerWidth || 800;
  const h = window.innerHeight || 600;
  const panelW = 260;
  const panelH = Math.min(560, Math.round(h * 0.7)); // matches maxHeight: 70vh
  const left = Math.max(8, Math.min(w - panelW - 8, w - panelW - 80));
  const top  = Math.max(8, Math.min(h - panelH - 8, h - panelH - 80));
  return { left, top };
}

export default function QuickWidgetLauncher() {
  const [open, setOpen] = useState(false);
  const [busyKind, setBusyKind] = useState(null);
  const [toast, setToast] = useState(null);
  // Recompute panel initial position every time it opens so it adapts to
  // window resizes (e.g. owner pops out the side panel).
  const [popupInitial, setPopupInitial] = useState(() => safePopupInitial());

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 2200);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const togglePanel = () => {
    if (!open) setPopupInitial(safePopupInitial());
    setOpen(o => !o);
  };

  const add = async (w) => {
    setBusyKind(w.kind);
    try {
      const body = { kind: w.kind, position: w.pos, size: w.size, ...(w.extra || {}) };
      await api.post("/widgets", body);
      window.dispatchEvent(new Event("rawco-widgets-changed"));
      setToast(`✓ ${w.label} added`);
    } catch (e) {
      setToast(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusyKind(null); }
  };

  return (
    <>
      {/* Drag-able toggle button. Default position is ABOVE the
          'Made with Emergent' badge (which sits bottom-right and has
          z-index 9999) so the button is always clickable on a fresh
          install. Owner can drag it anywhere afterwards. */}
      <DraggableFloater
        storageKey="rawco-quick-widget-btn-pos"
        initial={{ right: 16, bottom: 96 }}
        zIndex={101}
        testid="quick-widget-floater"
      >
        <button
          onClick={togglePanel}
          title="Quick add widget"
          data-testid="quick-widget-button"
          className="w-12 h-12 rounded-full flex items-center justify-center font-display transition-all hover:scale-110"
          style={{
            background: "rgba(0,4,12,0.94)", border: `1px solid var(--rc-gold)`,
            color: "var(--rc-gold)", boxShadow: "0 0 18px rgba(255,184,0,0.35)",
          }}
        >
          <span className="text-2xl leading-none">{open ? "×" : "+"}</span>
        </button>
      </DraggableFloater>

      {/* Drag-able popup — re-keyed by `open` so initial position recomputes */}
      {open && (
        <DraggableFloater
          key={`qwl-${open}`}
          storageKey="rawco-quick-widget-panel-pos"
          initial={popupInitial}
          zIndex={100}
          testid="quick-widget-panel-floater"
        >
          <div
            className="rc-panel p-3 grid grid-cols-2 gap-2 w-[260px]"
            data-testid="quick-widget-panel"
            style={{ background: "rgba(0,4,12,0.96)", border: "1px solid var(--rc-gold)", maxHeight: "70vh", overflowY: "auto" }}
          >
            <div className="col-span-2 flex items-center justify-between mb-1">
              <span className="rc-label text-[10px] opacity-70">// QUICK ADD WIDGET — drag me</span>
              <button
                onClick={() => setOpen(false)}
                data-testid="quick-widget-close"
                className="text-[var(--rc-gold)] text-xs leading-none px-1.5 py-0.5 border border-[var(--rc-gold-dim)] hover:bg-white/5"
                title="Close"
              >×</button>
            </div>
            {QUICK_WIDGETS.map((w) => (
              <button
                key={w.kind}
                onClick={() => add(w)}
                disabled={busyKind === w.kind}
                className="border border-[var(--rc-gold-dim)] hover:border-[var(--rc-gold)] hover:bg-[var(--rc-gold-dim)] hover:bg-opacity-20 transition-all p-2 text-left"
                data-testid={`quick-widget-${w.kind}`}
                style={{ opacity: busyKind === w.kind ? 0.5 : 1 }}
              >
                <div className="font-display text-xs">{w.icon} {w.label}</div>
                <div className="text-[9px] opacity-50 mt-0.5">{w.pos.replace("_", " ")} · {w.size}</div>
              </button>
            ))}
            <button onClick={() => { setOpen(false); window.location.hash = "#settings-widgets"; window.dispatchEvent(new CustomEvent("rawco-open-settings", { detail: { tab: "widgets" } })); }}
              className="col-span-2 rc-label text-[9px] opacity-70 hover:opacity-100 mt-1 text-center"
              data-testid="quick-widget-open-settings">
              … more in Settings → Widgets
            </button>
          </div>
        </DraggableFloater>
      )}

      {toast && (
        <div className="fixed bottom-20 right-4 z-[105] rc-panel px-3 py-2 text-[11px] rc-glow-text"
          style={{ background: "rgba(0,4,12,0.95)", border: "1px solid var(--rc-gold)" }}
          data-testid="quick-widget-toast">{toast}</div>
      )}
    </>
  );
}
